import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { handleGetRequest, handlePatchRequest } from '../../utils/api-handler.util'
import { formatTime } from '../../utils/date.util'
import SupportTicketStatus from '../../components/admin/SupportTicketStatus'
import UserTypeDisplay from '../../components/common/UserTypeDisplay'
import SupportTicketConversation from './SupportTicketConversation'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import InfoGrid from '../../components/common/InfoGrid'
import { SUPPORT_TICKET_STATUS } from '../../constants/supportTicket'
import SelectField from '../../components/common/SelectField'
import { showToaster } from '../../utils/show-toaster.util'
import TextareaField from '../../components/common/TextareaField'

const SupportTicketView = () => {
  const { ticketId } = useParams()

  const { data: response } = useQuery({
    queryKey: QueryKeys.adminSupportTicketDetails(ticketId),
    queryFn: () => handleGetRequest(`/admin/support-tickets/${ticketId}`),
    enabled: !!ticketId,
  })

  const ticket = response?.data
  const [newStatus, setNewStatus] = useState('')
  const [ticketResolutionNote, setTicketResolutionNote] = useState('')

  const updateStatusMutation = useMutation({
    mutationFn: (payload) =>
      handlePatchRequest(`/admin/support-tickets/${ticketId}/update-status`, payload),
    onSuccess: () => {
      QueryKeys.adminSupportTickets.invalidateAll()
      QueryKeys.adminSupportTicketDetails.invalidate(ticketId)
      setNewStatus('')
    },
    onError: () => {
      showToaster('error', 'Failed to update support ticket status')
    },
  })

  if (!ticket) {
    return null
  }

  const ticketInfoItems = [
    {
      label: 'Name',
      value: (
        <span>
          {ticket.userName} - <UserTypeDisplay userType={ticket.userType} />
        </span>
      ),
    },
    { label: 'Phone', value: ticket.userPhone || '-' },
    {
      label: 'Attachment',
      value: ticket.attachmentUrl ? (
        <a
          href={ticket.attachmentUrl}
          className="block text-tertiary-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {ticket.attachmentUrl.split('/').pop()}
        </a>
      ) : (
        <span className="text-neutral-500">No document</span>
      ),
    },
    { label: 'Created At', value: formatTime(ticket.createdAt) },
    ...(ticket?.status === SUPPORT_TICKET_STATUS.resolved.key
      ? [
          {
            label: 'Resolution',
            value: ticket.resolution,
          },
          {
            label: 'Resolved By',
            value: ticket.resolver?.name,
          },
          {
            label: 'Resolved At',
            value: formatTime(ticket.resolvedAt),
          },
        ]
      : []),
  ]

  const getActions = () => {
    return (
      <div>
        <h2 className="text-lg font-bold text-left mb-2">Update Status</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            updateStatusMutation.mutate({ status: newStatus, resolution: ticketResolutionNote })
          }}
        >
          <SelectField
            value={newStatus}
            onChange={(e) => {
              setNewStatus(e.target.value)
            }}
            required
          >
            <option value="" disabled>
              Select Status
            </option>
            {Object.values(SUPPORT_TICKET_STATUS).map((status) => (
              <option
                key={status.key}
                value={status.key}
                disabled={status.key === SUPPORT_TICKET_STATUS.pending.key}
              >
                {status.label}
              </option>
            ))}
          </SelectField>

          {newStatus === SUPPORT_TICKET_STATUS.resolved.key && (
            <div className="mt-4">
              <TextareaField
                label="Resolution "
                value={ticketResolutionNote}
                onChange={(e) => setTicketResolutionNote(e.target.value)}
                placeholder="Add resolution note"
                required
              />
            </div>
          )}

          <button
            type="submit"
            className="bg-secondary-600 text-white px-6 py-2 rounded-md text-sm  transition-colors font-medium mt-2"
          >
            {updateStatusMutation.isPending ? 'Updating...' : 'Update Status'}
          </button>
        </form>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="flex items-center justify-between px-8 py-6 border-b border-black/20">
        <h1 className="text-2xl font-bold text-foreground-secondary flex items-center gap-2">
          Support Ticket #{ticketId} - <SupportTicketStatus status={ticket.status} />
        </h1>
      </div>

      <div className="my-6">
        <InfoGrid infoItems={ticketInfoItems} actions={getActions()} />
      </div>

      <SupportTicketConversation ticket={ticket} />
    </div>
  )
}

export default SupportTicketView
